import React, { useState, useEffect, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';
import { isAoAdmin } from './AoSociete';

/**
 * Règles de notification (module CRM v2, back PR #387/#402).
 *
 * Une règle = un déclencheur + un template d'email + des destinataires.
 * Elle naît INACTIVE : rien ne part tant qu'on ne l'active pas, et l'écran
 * le dit plutôt que de laisser croire qu'une règle créée est en service.
 *
 * Deux principes tenus ici :
 *  - le catalogue publié par le serveur fait foi. Les déclencheurs, leurs
 *    variables et les canaux opérationnels viennent de lui ; le front n'en
 *    invente aucun, et masque les canaux non branchés ;
 *  - un déclencheur ÉVÉNEMENTIEL n'a ni heure ni rappel (il part sur
 *    l'événement), et son test à blanc annonce « 0 concerné » — l'écran
 *    reprend la note du serveur au lieu de laisser croire à une panne.
 *
 * Routes : GET /crm/notifications/catalogue ;
 *          GET/POST/PUT/DELETE /crm/notifications/regles ;
 *          GET/POST/PUT/DELETE /email-templates (bibliothèque CENTRALE :
 *            les mêmes modèles servent aux convocations d'entretien — un
 *            modèle écrit ici est disponible partout dans l'ERP) ;
 *          POST /email-templates/apercu ;
 *          POST /crm/notifications/regles/:id/test (?envoyer=1 = envoi réel).
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const INPUT = { boxSizing: 'border-box', width: '100%', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '9px 11px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };
const LBL = { display: 'block', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '6px' };
const GHOST = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' };
const PLEIN = { ...GHOST, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' };
const mono = { fontFamily: "'IBM Plex Mono',monospace" };

// Libellés des réglages publiés par le catalogue ; une clé inconnue s'affiche
// telle quelle plutôt que d'être masquée.
const LIB_PARAM = {
  jour_du_mois: 'Jour du mois',
  apres_jours: 'Après (jours)',
  avant_jours: 'Avant (jours)',
};

const lire = (j) => j?.data ?? j;
const tab = (x) => (Array.isArray(x) ? x : []);
const emails = (s) => String(s || '').split(/[,;\n]/).map(e => e.trim()).filter(Boolean);

const Puce = ({ ton = 'mut', children, title }) => {
  const T = { ok: { bg: 'rgba(22,163,74,0.13)', fg: '#16a34a' }, warn: { bg: 'rgba(217,119,6,0.13)', fg: '#b45309' }, mut: { bg: 'var(--chip-bg)', fg: 'var(--text-2)' } }[ton];
  return <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: T.bg, color: T.fg, whiteSpace: 'nowrap' }}>{children}</span>;
};

/**
 * Destinataires ajoutés d'office par le serveur, en plus de ceux de la règle.
 * Publiés par le catalogue quand il les expose ; à défaut, la règle confirmée
 * par le back pour les deux déclencheurs Sourcing.
 */
const destinatairesAuto = (d) => {
  const publie = d?.destinataires_auto;
  if (typeof publie === 'string' && publie.trim()) return publie.trim();
  if (Array.isArray(publie) && publie.length) return publie.join(', ');
  if (/^sourcing/.test(String(d?.id || ''))) {
    return 'le créateur de la recherche et le responsable du besoin sont ajoutés automatiquement';
  }
  return null;
};

export default function NotificationsRegles({ showNotification }) {
  const [cat, setCat] = useState(null);        // catalogue : null = chargement
  const [regles, setRegles] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [form, setForm] = useState(null);      // règle en création/édition
  const [tplForm, setTplForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testRes, setTestRes] = useState(null); // { id, ...réponse }
  const [apercu, setApercu] = useState(null);   // rendu du template
  const admin = isAoAdmin(APIService.getCurrentUser());

  // Les listes sont relues séparément : la réponse d'un POST ne porte pas
  // toujours l'objet complet, on ne devine pas son contenu.
  const relireListes = useCallback(async () => {
    try {
      const [rr, rt] = await Promise.all([
        fetch(`${BASE}/crm/notifications/regles`, { headers: authH() }),
        fetch(`${BASE}/email-templates`, { headers: authH() }),
      ]);
      if (rr.ok) { const j = lire(await rr.json().catch(() => ({}))); setRegles(tab(j?.regles ?? j)); }
      if (rt.ok) { const j = lire(await rt.json().catch(() => ({}))); setTemplates(tab(j?.templates ?? j)); }
    } catch { /* l'écran garde ce qu'il a */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const rc = await fetch(`${BASE}/crm/notifications/catalogue`, { headers: authH() });
        if (rc.status === 404 || rc.status === 501) { setAbsent(true); setCat({}); setRegles([]); setTemplates([]); return; }
        setCat(lire(await rc.json().catch(() => ({}))) || {});
        await relireListes();
        setRegles(prev => (prev == null ? [] : prev));
        setTemplates(prev => (prev == null ? [] : prev));
      } catch { setAbsent(true); setCat({}); setRegles([]); setTemplates([]); }
    })();
  }, [relireListes]);

  const declencheurs = tab(cat?.declencheurs ?? cat);
  const canauxOk = tab(cat?.canaux_operationnels).length ? tab(cat.canaux_operationnels) : ['email'];
  const decl = (id) => declencheurs.find(d => String(d.id) === String(id)) || null;

  const envoyer = async (url, method, body, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(url, { method, headers: jsonH(), body: body ? JSON.stringify(body) : undefined });
      if (r.status === 404 || r.status === 501) { showNotification?.('Cette action arrive côté serveur — rien n\'a été enregistré.', 'error'); return false; }
      const j = await r.json().catch(() => ({}));
      // Les messages du serveur sont écrits pour l'utilisateur : tels quels.
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return false; }
      showNotification?.(j.message || okMsg, 'success');
      // Le serveur peut prévenir qu'une règle active n'enverra rien faute de
      // template : son texte est affiché tel quel, jamais résumé.
      const avert = j.avertissement ?? j.data?.avertissement;
      if (avert) showNotification?.(avert, 'error');
      await relireListes();
      return true;
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); return false; }
    finally { setBusy(false); }
  };

  const enregistrerRegle = async () => {
    if (!form.titre.trim()) { showNotification?.('Donnez un titre à la règle.', 'error'); return; }
    if (!form.declencheur) { showNotification?.('Choisissez un déclencheur.', 'error'); return; }
    const d = decl(form.declencheur);
    const edit = form.id != null;
    const corps = {
      titre: form.titre.trim(),
      declencheur: form.declencheur,
      destinataires: emails(form.destinataires),
      template_id: form.template_id || null,
      canaux: form.canaux,
    };
    // `actif` est IGNORÉ à la création (la règle naît toujours inactive) :
    // ne l'envoyer qu'en modification évite de promettre une activation.
    if (edit) corps.actif = form.actif === true;
    // Heure, rappels et paramètres n'ont de sens que sur un planifiable.
    if (!d?.evenementiel) {
      if (form.heure) corps.heure = form.heure;
      if (form.rappel_jours !== '') corps.rappel_jours = Number(form.rappel_jours) || 0;
      if (form.rappel_max !== '') corps.rappel_max = Number(form.rappel_max) || 0;
      const par = {};
      Object.keys(form.parametres || {}).forEach(k => {
        const v = form.parametres[k];
        if (v !== '' && v != null) par[k] = Number.isNaN(Number(v)) ? v : Number(v);
      });
      if (Object.keys(par).length) corps.parametres = par;
    }
    const ok = await envoyer(
      `${BASE}/crm/notifications/regles${edit ? `/${form.id}` : ''}`,
      edit ? 'PUT' : 'POST', corps,
      edit ? 'Règle mise à jour.' : 'Règle créée — elle est inactive tant que vous ne l\'activez pas.',
    );
    if (ok) setForm(null);
  };

  const basculerActif = (r) => envoyer(`${BASE}/crm/notifications/regles/${r.id}`, 'PUT',
    { actif: !r.actif }, r.actif ? 'Règle désactivée.' : 'Règle activée.');

  const supprimer = async (r) => {
    if (!await confirmErpia({ message: `Supprimer la règle « ${r.titre || r.nom || r.id} » ?` })) return;
    envoyer(`${BASE}/crm/notifications/regles/${r.id}`, 'DELETE', null, 'Règle supprimée.');
  };

  const tester = async (r, reel) => {
    if (reel && !await confirmErpia({ message: `Envoyer réellement l'email de « ${r.titre || r.id} » aux destinataires ?` })) return;
    setBusy(true); setTestRes(null);
    try {
      const rr = await fetch(`${BASE}/crm/notifications/regles/${r.id}/test${reel ? '?envoyer=1' : ''}`, { method: 'POST', headers: jsonH() });
      if (rr.status === 403) {
        const j403 = await rr.json().catch(() => ({}));
        showNotification?.(j403.error || j403.message || 'L\'envoi réel est réservé aux administrateurs.', 'error');
        return;
      }
      const j = lire(await rr.json().catch(() => ({}))) || {};
      if (!rr.ok) { showNotification?.(j.error || j.message || `HTTP ${rr.status}`, 'error'); return; }
      setTestRes({ id: r.id, ...j });
    } catch (e) { showNotification?.('Test impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const apercuTpl = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/email-templates/apercu`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ objet: tplForm.objet, corps: tplForm.corps, valeurs: {} }),
      });
      const j = lire(await r.json().catch(() => ({}))) || {};
      if (!r.ok) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      setApercu(j);
    } catch (e) { showNotification?.('Aperçu impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const enregistrerTpl = async () => {
    // Les trois champs sont requis à la création côté serveur : on le dit ici
    // plutôt que de laisser partir une requête qui sera refusée.
    if (!tplForm.nom.trim() || !tplForm.objet.trim() || !tplForm.corps.trim()) {
      showNotification?.('Nom, objet et message sont requis.', 'error'); return;
    }
    const edit = tplForm.id != null;
    const ok = await envoyer(
      `${BASE}/email-templates${edit ? `/${tplForm.id}` : ''}`,
      edit ? 'PUT' : 'POST',
      { nom: tplForm.nom.trim(), objet: tplForm.objet, corps: tplForm.corps },
      edit ? 'Template mis à jour.' : 'Template créé.',
    );
    if (ok) setTplForm(null);
  };

  if (cat === null) {
    return <div style={{ ...CARD, padding: '24px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
      <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'nr-spin .8s linear infinite', marginRight: '7px' }} /> Chargement des règles…
      <style>{'@keyframes nr-spin{to{transform:rotate(360deg)}}'}</style>
    </div>;
  }
  if (absent) {
    return <div style={{ ...CARD, padding: '18px 20px', fontSize: '12.5px', color: 'var(--text-2)' }}>
      <BI icon="hourglass-split" style={{ marginRight: '7px' }} />
      Les règles de notification arrivent côté serveur — l'écran est prêt.
    </div>;
  }

  const dForm = form ? decl(form.declencheur) : null;
  const varsForm = tab(dForm?.variables);

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <style>{'@keyframes nr-spin{to{transform:rotate(360deg)}}'}</style>

      {/* ── Règles ── */}
      <div style={{ ...CARD, padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, fontFamily: "'Archivo',sans-serif", fontSize: '15px', fontWeight: 800, color: 'var(--text-strong)' }}>Règles de notification</span>
          <button onClick={() => setForm({ titre: '', declencheur: '', destinataires: '', template_id: '', canaux: [canauxOk[0]], heure: '', rappel_jours: '', rappel_max: '', parametres: {}, actif: false })}
            style={PLEIN}><BI icon="plus-lg" /> Nouvelle règle</button>
        </div>

        {tab(regles).length === 0 ? (
          <div style={{ padding: '22px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '12.5px' }}>
            Aucune règle. Créez-en une sur un déclencheur, associez-lui un template d'email, puis activez-la.
          </div>
        ) : tab(regles).map((r, i) => {
          const d = decl(r.declencheur);
          const tpl = tab(templates).find(t => String(t.id) === String(r.template_id));
          const dest = tab(r.destinataires);
          return (
            <div key={r.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: i ? '1px solid var(--hair)' : 'none', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '210px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)' }}>{r.titre || r.nom || `Règle ${r.id}`}</div>
                <div style={{ marginTop: '2px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                  {[d?.label || r.declencheur_label || r.declencheur, tpl ? `template « ${tpl.nom} »` : 'aucun template',
                    dest.length ? `${dest.length} destinataire${dest.length > 1 ? 's' : ''}` : 'aucun destinataire fixe'].filter(Boolean).join(' · ')}
                </div>
                {/* Journal de bord de la règle : ce que le serveur a fait au
                    dernier passage, dit avec ses mots. */}
                {r.dernier_resultat && (
                  <div style={{ marginTop: '2px', fontSize: '11px', color: 'var(--text-faint)' }}>
                    Dernier passage{r.dernier_envoi ? ` (${new Date(r.dernier_envoi).toLocaleDateString('fr-CH')})` : ''} : {String(r.dernier_resultat)}
                  </div>
                )}
              </div>
              {d?.evenementiel && <Puce title="Cette règle part sur l'événement, pas à heure fixe.">sur événement</Puce>}
              {r.actif && !r.template_id && <Puce ton="warn" title="Une règle active sans template n'envoie rien.">sans template</Puce>}
              <Puce ton={r.actif ? 'ok' : 'mut'}>{r.actif ? 'Active' : 'Inactive'}</Puce>
              <button onClick={() => tester(r, false)} disabled={busy} style={{ ...GHOST, padding: '6px 10px', fontSize: '12px' }}>Tester sans envoyer</button>
              {admin && (
                <button onClick={() => tester(r, true)} disabled={busy} title="Envoie réellement l'email aux destinataires de la règle."
                  style={{ ...GHOST, padding: '6px 10px', fontSize: '12px' }}>Envoyer un vrai test</button>
              )}
              <button onClick={() => basculerActif(r)} disabled={busy} style={{ ...(r.actif ? GHOST : PLEIN), padding: '6px 10px', fontSize: '12px' }}>
                {r.actif ? 'Désactiver' : 'Activer'}
              </button>
              <button onClick={() => setForm({
                id: r.id, titre: r.titre || r.nom || '', declencheur: r.declencheur || '',
                destinataires: dest.join(', '), template_id: r.template_id ?? '',
                canaux: tab(r.canaux).length ? tab(r.canaux) : [canauxOk[0]],
                heure: r.heure || '', rappel_jours: r.rappel_jours ?? '', rappel_max: r.rappel_max ?? '',
                parametres: r.parametres && typeof r.parametres === 'object' ? r.parametres : {}, actif: !!r.actif,
              })} style={{ ...GHOST, padding: '6px 10px', fontSize: '12px' }}>Modifier</button>
              <button onClick={() => supprimer(r)} style={{ ...GHOST, padding: '6px 10px', fontSize: '12px', color: 'var(--danger-fg, #cf3b32)' }}>Supprimer</button>

              {testRes?.id === r.id && (
                <div style={{ width: '100%', marginTop: '8px', padding: '10px 12px', borderRadius: '9px', background: 'var(--code-bg)', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                  <b style={{ color: 'var(--text-strong)' }}>Test à blanc</b>
                  {testRes.concernes != null && <> — {testRes.concernes} concerné{testRes.concernes > 1 ? 's' : ''}</>}
                  {/* Sur une règle événementielle, « 0 concerné » est normal :
                      la note du serveur l'explique, on l'affiche telle quelle. */}
                  {testRes.note && <div style={{ marginTop: '4px' }}>{testRes.note}</div>}
                  {testRes.message && !testRes.note && <div style={{ marginTop: '4px' }}>{testRes.message}</div>}
                  {tab(testRes.variables_manquantes).length > 0 && (
                    <div style={{ marginTop: '4px', color: '#b45309' }}>
                      Variables sans valeur, laissées vides : {tab(testRes.variables_manquantes).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Formulaire de règle ── */}
      {form && (
        <div onClick={() => !busy && setForm(null)} style={{ position: 'fixed', inset: 0, zIndex: 620, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(560px,96vw)', maxHeight: '90vh', overflowY: 'auto', ...CARD, background: 'var(--panel, #fff)', borderRadius: '14px', padding: '20px 22px' }}>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '17px', fontWeight: 800, color: 'var(--text-strong)', marginBottom: '14px' }}>
              {form.id != null ? 'Modifier la règle' : 'Nouvelle règle'}
            </div>

            <label style={LBL}>Titre</label>
            <input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} style={INPUT} autoFocus
              placeholder="Sélection Sourceur prête" />

            <label style={{ ...LBL, marginTop: '12px' }}>Déclencheur</label>
            <ErpiaSelect value={form.declencheur} onChange={e => setForm(f => ({ ...f, declencheur: e.target.value }))} style={INPUT}>
              <option value="">Choisir…</option>
              {declencheurs.map(d => <option key={d.id} value={d.id}>{d.label || d.id}</option>)}
            </ErpiaSelect>
            {dForm?.description && <div style={{ marginTop: '5px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>{dForm.description}</div>}
            {varsForm.length > 0 && (
              <div style={{ marginTop: '7px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>Variables utilisables dans le template :</div>
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {varsForm.map((v, i) => <code key={i} style={{ ...mono, fontSize: '11px', padding: '2px 7px', borderRadius: '5px', background: 'var(--code-bg)', color: 'var(--text-2)' }}>{typeof v === 'string' ? v : (v.cle || v.nom || '')}</code>)}
                </div>
              </div>
            )}

            <label style={{ ...LBL, marginTop: '12px' }}>Template d'email</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ErpiaSelect value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))} style={INPUT}>
                <option value="">Choisir…</option>
                {tab(templates).map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </ErpiaSelect>
              <button onClick={() => setTplForm({ nom: '', objet: '', corps: '' })} style={{ ...GHOST, flex: 'none' }}>Nouveau</button>
            </div>
            {!form.template_id && <div style={{ marginTop: '5px', fontSize: '11.5px', color: '#b45309' }}>Sans template, la règle n'enverra rien.</div>}

            <label style={{ ...LBL, marginTop: '12px' }}>Destinataires</label>
            <textarea value={form.destinataires} onChange={e => setForm(f => ({ ...f, destinataires: e.target.value }))} rows={2}
              placeholder="prenom.nom@exemple.ch, autre@exemple.ch" style={{ ...INPUT, resize: 'vertical' }} />
            <div style={{ marginTop: '5px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
              Adresses séparées par des virgules.
              {destinatairesAuto(dForm) && <> Pour ce déclencheur, {destinatairesAuto(dForm)} — en plus de cette liste.</>}
            </div>

            {/* Heure et rappel : uniquement sur un déclencheur planifiable. */}
            {dForm && !dForm.evenementiel && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div><label style={LBL}>Heure d'envoi</label><input type="time" value={form.heure} onChange={e => setForm(f => ({ ...f, heure: e.target.value }))} style={INPUT} /></div>
                  <div><label style={LBL} title="Nombre de jours entre deux rappels.">Rappel (jours)</label><input type="number" min="0" value={form.rappel_jours} onChange={e => setForm(f => ({ ...f, rappel_jours: e.target.value }))} style={{ ...INPUT, ...mono }} /></div>
                  <div><label style={LBL} title="Nombre de rappels au-delà duquel on s'arrête.">Rappels max.</label><input type="number" min="0" value={form.rappel_max} onChange={e => setForm(f => ({ ...f, rappel_max: e.target.value }))} style={{ ...INPUT, ...mono }} /></div>
                </div>
                {/* Réglages propres au déclencheur : les clés viennent des
                    valeurs par défaut publiées par le catalogue — le front
                    n'en invente aucune. */}
                {Object.keys(dForm?.defaut || {}).length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={LBL}>Réglages du déclencheur</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                      {Object.keys(dForm.defaut).map(k => (
                        <div key={k}>
                          <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginBottom: '4px' }}>{LIB_PARAM[k] || k}</div>
                          <input value={form.parametres?.[k] ?? ''} placeholder={String(dForm.defaut[k] ?? '')}
                            onChange={e => setForm(f => ({ ...f, parametres: { ...(f.parametres || {}), [k]: e.target.value } }))}
                            style={{ ...INPUT, ...mono }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '5px', fontSize: '11.5px', color: 'var(--text-3)' }}>Laissé vide, la valeur par défaut du déclencheur s'applique.</div>
                  </div>
                )}
              </>
            )}
            {dForm?.evenementiel && (
              <div style={{ marginTop: '12px', padding: '9px 12px', borderRadius: '8px', background: 'var(--code-bg)', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.55 }}>
                Ce déclencheur part sur l'événement lui-même : ni heure d'envoi ni rappel à régler.
              </div>
            )}

            {/* Canaux : seuls ceux que le serveur déclare opérationnels. */}
            {canauxOk.length > 1 && (
              <div style={{ marginTop: '12px' }}>
                <label style={LBL}>Canaux</label>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {canauxOk.map(c => {
                    const on = form.canaux.includes(c);
                    return <button key={c} type="button" onClick={() => setForm(f => ({ ...f, canaux: on ? f.canaux.filter(x => x !== c) : [...f.canaux, c] }))}
                      style={{ ...GHOST, padding: '5px 11px', fontSize: '12px', ...(on ? { background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none' } : {}) }}>{c}</button>;
                  })}
                </div>
              </div>
            )}

            <div style={{ marginTop: '14px', fontSize: '11.5px', color: 'var(--text-3)' }}>
              {form.id == null ? 'La règle est créée inactive : rien ne part tant que vous ne l\'activez pas.' : null}
            </div>
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setForm(null)} disabled={busy} style={GHOST}>Annuler</button>
              <button onClick={enregistrerRegle} disabled={busy} style={{ ...PLEIN, opacity: busy ? 0.6 : 1 }}>
                <BI icon="check-lg" /> {form.id != null ? 'Enregistrer' : 'Créer la règle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Templates d'email du module ── */}
      <div style={{ ...CARD, padding: '16px 18px', marginTop: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, fontFamily: "'Archivo',sans-serif", fontSize: '15px', fontWeight: 800, color: 'var(--text-strong)' }}>Templates d'email</span>
          <button onClick={() => setTplForm({ nom: '', objet: '', corps: '' })} style={GHOST}><BI icon="plus-lg" /> Nouveau template</button>
        </div>
        {tab(templates).length === 0
          ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun template — créez-en un, il sera proposé dans chaque règle.</div>
          : tab(templates).map((t, i) => (
            <div key={t.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderTop: i ? '1px solid var(--hair)' : 'none', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{t.nom}</div>
                {t.objet && <div style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>{t.objet}</div>}
              </div>
              <button onClick={() => setTplForm({ id: t.id, nom: t.nom || '', objet: t.objet || '', corps: t.corps || '' })} style={{ ...GHOST, padding: '6px 10px', fontSize: '12px' }}>Modifier</button>
            </div>
          ))}
      </div>

      {tplForm && (
        <div onClick={() => !busy && setTplForm(null)} style={{ position: 'fixed', inset: 0, zIndex: 630, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(600px,96vw)', maxHeight: '90vh', overflowY: 'auto', ...CARD, background: 'var(--panel, #fff)', borderRadius: '14px', padding: '20px 22px' }}>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '17px', fontWeight: 800, color: 'var(--text-strong)', marginBottom: '14px' }}>
              {tplForm.id != null ? 'Modifier le template' : 'Nouveau template'}
            </div>
            <label style={LBL}>Nom</label>
            <input value={tplForm.nom} onChange={e => setTplForm(f => ({ ...f, nom: e.target.value }))} style={INPUT} autoFocus />
            <label style={{ ...LBL, marginTop: '12px' }}>Objet</label>
            <input value={tplForm.objet} onChange={e => setTplForm(f => ({ ...f, objet: e.target.value }))} style={INPUT}
              placeholder="Sourcing {besoin} : sélection prête" />
            <label style={{ ...LBL, marginTop: '12px' }}>Message</label>
            <textarea value={tplForm.corps} onChange={e => setTplForm(f => ({ ...f, corps: e.target.value }))} rows={7}
              style={{ ...INPUT, resize: 'vertical', lineHeight: 1.6 }} placeholder={'{nombre} candidat(s) trouvés — {mode}.'} />
            <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
              Les variables disponibles dépendent du déclencheur de la règle ; elles sont listées à la
              création de la règle. Une variable sans valeur reste vide — jamais inventée.
            </div>
            {apercu && (
              <div style={{ marginTop: '12px', padding: '11px 13px', borderRadius: '9px', background: 'var(--code-bg)', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{apercu.objet || '(objet vide)'}</div>
                <div style={{ marginTop: '6px', whiteSpace: 'pre-line' }}>{apercu.corps || ''}</div>
                {tab(apercu.variables_manquantes).length > 0 && (
                  <div style={{ marginTop: '7px', color: '#b45309' }}>
                    Sans valeur dans cet aperçu, laissées vides : {tab(apercu.variables_manquantes).join(', ')}
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => { setTplForm(null); setApercu(null); }} disabled={busy} style={GHOST}>Annuler</button>
              <button onClick={apercuTpl} disabled={busy} style={GHOST}>Aperçu</button>
              <button onClick={enregistrerTpl} disabled={busy} style={{ ...PLEIN, opacity: busy ? 0.6 : 1 }}><BI icon="check-lg" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
